/**
 * Product Sync inventory coverage: the variants a coverage pass could not
 * link yet (BLU-1519).
 *
 * Coverage used to fail the WHOLE approved set when one variant was not
 * pushable, so no variant of the job got stock. It now links every good
 * variant and keeps only these pending, each with a reason. The backend
 * writes the list onto the source job's `stats.inventoryCoverage`; the job
 * page reads it back through `parseCoverageStuckVariants` (the stats are
 * JSONB written by older builds too, so the read is defensive).
 *
 * Shared so the reason codes the backend writes and the page translates can
 * never drift.
 */
export const COVERAGE_STUCK_REASONS = [
    /** Shopify "Track quantity" is off for the variant. */
    'not_tracked',
    /** Shopify "This is a physical product" is off for the variant. */
    'not_physical',
    /** The variant no longer exists in Shopify. */
    'variant_missing',
    /** PrintRun has no blank mapping for the variant any more. */
    'projection_missing',
    /** The variant's blank changed after this sync was approved. */
    'base_sku_changed',
    /** The variant's PrintRun SKU in Shopify no longer matches PrintRun. */
    'identity_mismatch',
    /** PrintRun tried to turn the two Shopify settings on and Shopify refused. */
    'enable_failed',
    /**
     * Track quantity / physical product is off, and PrintRun did not turn it on
     * because the shop's stock push does not write stock for this variant (push
     * off, Auto Sync off, Dry Run on, paused, or its blank/location excluded).
     */
    'push_not_live',
];
/**
 * Reasons another automatic pass can never clear: only a new Product sync
 * for the product can. A job left with only these stops retrying.
 */
export const COVERAGE_STUCK_NEEDS_SYNC_REASONS = [
    'variant_missing',
    'projection_missing',
    'base_sku_changed',
];
/** The job stats carry at most this many rows; the count fields carry the full total. */
export const MAX_STORED_COVERAGE_STUCK_VARIANTS = 100;
const REASON_SET = new Set(COVERAGE_STUCK_REASONS);
export function isCoverageStuckReason(value) {
    return typeof value === 'string' && REASON_SET.has(value);
}
const optionalText = (value) => typeof value === 'string' && value.trim() ? value : null;
/**
 * Defensive read of a stored stuck list. Drops any row without a variant id
 * or with an unknown reason (never guesses one), and keeps at most the stored
 * cap.
 */
export function parseCoverageStuckVariants(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value) {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw))
            continue;
        const row = raw;
        const variantId = optionalText(row.variantId);
        if (!variantId || !isCoverageStuckReason(row.reason))
            continue;
        const detail = optionalText(row.detail);
        const refusal = row.refusal === 'store_guard' || row.refusal === 'access_denied' ? row.refusal : null;
        out.push({
            variantId,
            productId: optionalText(row.productId),
            productTitle: optionalText(row.productTitle),
            variantTitle: optionalText(row.variantTitle),
            sku: optionalText(row.sku),
            reason: row.reason,
            ...(detail ? { detail } : {}),
            ...(refusal ? { refusal } : {}),
        });
        if (out.length >= MAX_STORED_COVERAGE_STUCK_VARIANTS)
            break;
    }
    return out;
}
/**
 * The numeric tail of a Shopify GID (`gid://shopify/ProductVariant/123` to
 * 123), or null when it is not a positive safe integer. Admin URLs take the
 * number, never the GID.
 */
export function shopifyGidNumericId(gid) {
    if (!gid)
        return null;
    const match = /^gid:\/\/shopify\/[A-Za-z]+\/(\d+)$/.exec(gid.trim());
    if (!match)
        return null;
    const id = Number(match[1]);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
}
